/**
 * Mobile tab route definitions — mirrors the web app's private_routes array.
 *
 * New structure (Account > Plans):
 *   AI       — account-level cross-plan AI chat (AccountChatScreen)
 *   Plans    — drill into a project → ChatScreen / HistoryScreen
 *   Settings — account-level settings (always visible)
 *
 * Flags mirror the web's private_routes shape:
 *   partner     — show for partner accounts
 *   user        — show for regular user accounts
 *   is_project  — if true, filter by project-level permissions (not used in tabs)
 *   permissions — account-level permissions required (empty = always show)
 *   condition   — optional function override; if present, its return value is used
 */
export interface TabRoute {
  name: string;
  title: string;
  /** FA icon name, e.g. 'sparkles', 'rectangle-list', 'cog' */
  icon: string;
  /** Account-level permissions required. Empty array = always visible. */
  permissions: string[];
  /** If true, the tab is hidden until at least one project is available. */
  requiresProject?: boolean;
  /** Show for partner accounts */
  partner?: boolean;
  /** Show for regular user accounts */
  user?: boolean;
  /** Optional condition function — overrides permission check if present */
  condition?: () => boolean;
  /**
   * GLOBAL platform feature flag (system_settings column) that must be ON
   * for this tab to appear. Tab is hidden when the flag is false. Composes
   * with account permissions — both must pass.
   */
  featureFlag?: string;
}

export const TAB_ROUTES: TabRoute[] = [
  {
    // Client-only Home landing. Listed first so it becomes the initial route
    // for client accounts (MainTabNavigator picks tabs[0]); partners route
    // through PartnerTabNavigator and never see this. `user`-only, no
    // permission gate — every client account lands here.
    name: 'Home',
    title: 'Home',
    icon: 'house',
    permissions: [],
    partner: false,
    user: true,
  },
  {
    name: 'AI',
    title: 'AI',
    icon: 'sparkles',
    permissions: [],
    partner: true,
    user: true,
    featureFlag: 'ai_account_chat_enabled',
  },
  {
    name: 'Plans',
    title: 'Plans',
    icon: 'rectangle-list',
    permissions: ['basic-user', 'account-chat'],
    partner: false,
    user: true,
  },
  {
    name: 'Settings',
    title: 'Settings',
    icon: 'cog',
    permissions: [],
    partner: true,
    user: true,
  },
];
